import { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import * as ExcelJS from 'exceljs';
import {
  AlertCircle,
  ArrowLeftRight,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Download,
  Hash,
  RefreshCw,
  Search,
  TrendingUp
} from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { useAuth } from '@/contexts/useAuth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ReportDateRangePicker } from './date-range/ReportDateRangePicker';
import { makePreset, type ReportPeriod } from './date-range/reportPeriod';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { createComponentLogger } from '@/utils/logger';
import type { RevenueReportLoan } from '@/services/bankerReconService';
import { getCompanyRevenueReportData } from '@/services/companyRevenueReportService';
import type {
  BrokeredRevenueRecord,
  CompanyRevenueReportData,
  ServicingCashEvent,
  ServicingPrincipalTieOut
} from '@/types/companyRevenueReport';
import {
  buildAccountingExportRows,
  buildCompanyRevenueDisplayTotals,
  buildUnifiedCompanyRevenueTotals,
  buildUnifiedRevenueMonthlyTrends,
  classifyWireDeduction,
  filterRevenueReportLoans,
  getClearingAccount,
  type UnifiedRevenueMonthBucket
} from './companyRevenueReportModel';

const log = createComponentLogger('CompanyRevenueReport', 'reports');

function formatCurrency(amount: number): string {
  const prefix = amount < 0 ? '-' : '';
  return `${prefix}$${Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

function formatDate(date: string | null): string {
  if (!date) {
    return '—';
  }

  return format(parseISO(date), 'MM/dd/yyyy');
}

// Whole-dollar, sign-aware (e.g. -$4,700). Used by the Trends table, chart tooltip, and card average.
function formatCurrency0(amount: number): string {
  const prefix = amount < 0 ? '-' : '';
  return `${prefix}$${Math.abs(amount).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

// Compact, sign-aware currency (e.g. $28.4M, $526K, -$4.7K). Used by the card volume and chart Y axis.
function formatCompactCurrency(amount: number): string {
  const sign = amount < 0 ? '-' : '';
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) {
    return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  }
  return `${sign}$${abs.toFixed(0)}`;
}

interface SummaryCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  colorClass?: string;
  subtitle?: React.ReactNode;
}

function SummaryCard({ title, value, icon, colorClass = 'text-foreground', subtitle }: SummaryCardProps) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className={cn('text-lg font-bold', colorClass)}>{value}</p>
            {subtitle && <p className="text-[11px] text-muted-foreground truncate">{subtitle}</p>}
          </div>
          <div className="p-2 bg-muted rounded-md">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

interface TrendsTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: UnifiedRevenueMonthBucket }>;
  label?: string | number;
}

function TrendsTooltip({ active, payload, label }: TrendsTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const bucket = payload[0]?.payload;
  if (!bucket) {
    return null;
  }

  return (
    <div className="rounded-lg border bg-card p-3 text-sm shadow-lg">
      <p className="font-medium mb-1">{label}</p>
      <p className="flex items-center justify-between gap-6">
        <span className="text-muted-foreground">Company Income</span>
        <span className={cn('font-mono', bucket.totalCompanyIncome >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
          {formatCurrency0(bucket.totalCompanyIncome)}
        </span>
      </p>
      <p className="flex items-center justify-between gap-6">
        <span className="text-muted-foreground">Banked</span>
        <span className="font-mono text-blue-600">{formatCurrency0(bucket.bankedContribution)}</span>
      </p>
      <p className="flex items-center justify-between gap-6">
        <span className="text-muted-foreground">Brokered</span>
        <span className="font-mono">{formatCurrency0(bucket.brokeredCompanyRevenue)}</span>
      </p>
      <p className="flex items-center justify-between gap-6 border-t mt-1 pt-1">
        <span className="text-muted-foreground">Servicing</span>
        <span className="font-mono">{formatCurrency0(bucket.servicingIncome)}</span>
      </p>
    </div>
  );
}

// Wire In / Less LO Commissions / Net Wire read as one arithmetic group, so they are
// bracketed by a single hairline on each edge. Keep these in sync with the header,
// body and TOTAL rows of the Loan Detail table.
const WIRE_GROUP_START = 'border-l border-border';
const WIRE_GROUP_END = 'border-r border-border';

/**
 * LO compensation is a deduction from the wire, so a positive charge renders in
 * parentheses. `computeBankedRecon` can return a negative loPayout (a compensation
 * deduction offset that flows back to us); that renders as a plain credit rather than
 * a double-negative "(-$…)".
 */
function formatWireDeduction(amount: number): string {
  const disposition = classifyWireDeduction(amount);
  if (disposition === 'zero') {
    return '—';
  }

  return disposition === 'deduction'
    ? `(${formatCurrency(amount)})`
    : formatCurrency(-amount);
}

/** Keep the color treatment on the same cent-resolved classification as the text. */
function getWireDeductionColorClass(amount: number): string {
  const disposition = classifyWireDeduction(amount);
  if (disposition === 'credit') {
    return 'text-green-600';
  }
  if (disposition === 'deduction') {
    return 'text-red-600';
  }

  return 'text-muted-foreground';
}

function DetailRow({
  loan,
  isOpen,
  onToggle
}: {
  loan: RevenueReportLoan;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const decomposition = loan.decomposition;
  const accounting = loan.accounting;

  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onToggle}>
        <TableCell>
          <span className="flex items-center gap-1">
            {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <span className="font-mono text-sm">{loan.loanNumber}</span>
          </span>
        </TableCell>
        <TableCell className="text-sm">{loan.borrowerName}</TableCell>
        <TableCell className="text-sm">{loan.loName}</TableCell>
        <TableCell className="text-sm">{loan.investorName}</TableCell>
        <TableCell className="text-sm">{formatDate(loan.investorPurchaseDate)}</TableCell>
        <TableCell className={cn('text-right font-mono text-sm', WIRE_GROUP_START)}>
          {formatCurrency(accounting.wireReceived)}
        </TableCell>
        <TableCell
          className={cn(
            'text-right font-mono text-sm',
            getWireDeductionColorClass(loan.loCompensation)
          )}
        >
          {formatWireDeduction(loan.loCompensation)}
        </TableCell>
        <TableCell
          className={cn(
            'text-right font-mono text-sm font-semibold',
            WIRE_GROUP_END,
            loan.netWire >= 0 ? 'text-foreground' : 'text-red-700'
          )}
        >
          {formatCurrency(loan.netWire)}
        </TableCell>
        <TableCell className="text-right font-mono text-sm text-green-600">{formatCurrency(accounting.revenue)}</TableCell>
        <TableCell className="text-right font-mono text-sm text-red-600">{formatCurrency(accounting.expenses)}</TableCell>
        <TableCell className="text-right font-mono text-sm text-blue-600">{formatCurrency(accounting.passThrough)}</TableCell>
        <TableCell className="text-right font-mono text-sm text-teal-700">{formatCurrency(accounting.passThroughOutside)}</TableCell>
        <TableCell className={cn('text-right font-mono text-sm font-semibold', accounting.netProfit >= 0 ? 'text-green-700' : 'text-red-700')}>
          {formatCurrency(accounting.netProfit)}
        </TableCell>
      </TableRow>
      {isOpen && (
        <TableRow>
          <TableCell colSpan={13} className="bg-muted/30 p-0">
            <div className="px-8 py-4 grid grid-cols-3 gap-6 text-sm">
              <div>
                <h4 className="font-semibold text-green-700 mb-2">Revenue</h4>
                {decomposition.items
                  .filter((item) => item.bucket === 'revenue_in_wire')
                  .map((item, index) => (
                    <div key={`${item.description}-${index}`} className="flex justify-between text-xs py-0.5">
                      <span className="text-muted-foreground">{item.description}</span>
                      <span className="font-mono">{formatCurrency(item.wireEffect)}</span>
                    </div>
                  ))}
                {decomposition.items
                  .filter((item) => item.bucket === 'revenue_outside')
                  .map((item, index) => (
                    <div key={`${item.description}-${index}`} className="flex justify-between text-xs py-0.5">
                      <span className="text-muted-foreground">
                        {item.description}
                        <Badge variant="outline" className="text-[9px] ml-1">outside wire</Badge>
                      </span>
                      <span className="font-mono">{formatCurrency(item.amount)}</span>
                    </div>
                  ))}
              </div>

              <div>
                <h4 className="font-semibold text-red-700 mb-2">Expenses</h4>
                {decomposition.items
                  .filter((item) => item.bucket === 'expense_in_wire')
                  .map((item, index) => (
                    <div key={`${item.description}-${index}`} className="flex justify-between text-xs py-0.5">
                      <span className="text-muted-foreground">{item.description}</span>
                      <span className="font-mono">({formatCurrency(item.amount)})</span>
                    </div>
                  ))}
                {decomposition.items
                  .filter((item) => item.bucket === 'expense_outside')
                  .map((item, index) => (
                    <div key={`${item.description}-${index}`} className="flex justify-between text-xs py-0.5">
                      <span className="text-muted-foreground">
                        {item.description}
                        <Badge variant="outline" className="text-[9px] ml-1">outside wire</Badge>
                      </span>
                      <span className="font-mono">({formatCurrency(item.amount)})</span>
                    </div>
                  ))}
              </div>

              <div>
                <h4 className="font-semibold text-blue-700 mb-2">Pass-Through</h4>
                {decomposition.items
                  .filter((item) => item.bucket === 'pass_through_in_wire')
                  .map((item, index) => (
                    <div key={`${item.description}-${index}`} className="flex justify-between text-xs py-0.5">
                      <span className="text-muted-foreground">{item.description}</span>
                      <span className="font-mono">{formatCurrency(item.wireEffect)}</span>
                    </div>
                  ))}
                {decomposition.items
                  .filter((item) => item.bucket === 'pass_through_outside')
                  .map((item, index) => (
                    <div key={`${item.description}-${index}`} className="flex justify-between text-xs py-0.5">
                      <span className="text-muted-foreground">
                        {item.description}
                        <Badge variant="outline" className="text-[9px] ml-1">outside wire</Badge>
                      </span>
                      <span className="font-mono">{formatCurrency(item.wireEffect)}</span>
                    </div>
                  ))}
                {!decomposition.items.some((item) =>
                  item.bucket === 'pass_through_in_wire'
                  || item.bucket === 'pass_through_outside'
                ) && (
                  <p className="text-xs text-muted-foreground italic">None</p>
                )}
                {!decomposition.isWireBalanced && (
                  <div className="mt-2 flex items-center gap-1 text-amber-600">
                    <AlertCircle className="h-3 w-3" />
                    <span className="text-xs">Wire drift: {formatCurrency(accounting.wireReceived - decomposition.computedWire)}</span>
                  </div>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function BrokeredRevenueTable({ loans }: { loans: BrokeredRevenueRecord[] }) {
  if (loans.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">No finalized brokered loans found for this period.</div>;
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table className="min-w-[1180px]">
        <TableHeader>
          <TableRow>
            <TableHead>Finalized</TableHead>
            <TableHead>Loan #</TableHead>
            <TableHead>Borrower</TableHead>
            <TableHead>LO</TableHead>
            <TableHead>Lender</TableHead>
            <TableHead className="text-right">Gross Check</TableHead>
            <TableHead className="text-right">Reimbursements</TableHead>
            <TableHead className="text-right">Processing</TableHead>
            <TableHead className="text-right">Company Revenue</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loans.map((loan) => (
            <TableRow key={loan.id}>
              <TableCell>{new Date(loan.finalizedAt).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' })}</TableCell>
              <TableCell className="font-mono">{loan.loanNumber}</TableCell>
              <TableCell>{loan.borrowerName}</TableCell>
              <TableCell>{loan.loanOfficerName}</TableCell>
              <TableCell>{loan.lenderName}</TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(loan.grossCheckReceived)}</TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(loan.loReimbursements)}</TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(loan.processingFee)}</TableCell>
              <TableCell className="text-right font-mono font-semibold text-emerald-600">{formatCurrency(loan.companyRevenue)}</TableCell>
              <TableCell><Badge variant="outline">{loan.status}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={5} className="font-bold">TOTAL ({loans.length} loans)</TableCell>
            <TableCell className="text-right font-mono font-bold">{formatCurrency(loans.reduce((sum, loan) => sum + loan.grossCheckReceived, 0))}</TableCell>
            <TableCell className="text-right font-mono font-bold">{formatCurrency(loans.reduce((sum, loan) => sum + loan.loReimbursements, 0))}</TableCell>
            <TableCell className="text-right font-mono font-bold">{formatCurrency(loans.reduce((sum, loan) => sum + loan.processingFee, 0))}</TableCell>
            <TableCell className="text-right font-mono font-bold text-emerald-600">{formatCurrency(loans.reduce((sum, loan) => sum + loan.companyRevenue, 0))}</TableCell>
            <TableCell />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}

function ServicingRevenueTable({ events }: { events: ServicingCashEvent[] }) {
  if (events.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">No servicing cash events found for this receipt-date period.</div>;
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table className="min-w-[1180px]">
        <TableHeader>
          <TableRow>
            <TableHead>Received / Reversed</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>Loan #</TableHead>
            <TableHead>Borrower</TableHead>
            <TableHead>Payment #</TableHead>
            <TableHead className="text-right">Principal Clearing</TableHead>
            <TableHead className="text-right">Interest Income</TableHead>
            <TableHead className="text-right">Escrow Clearing</TableHead>
            <TableHead className="text-right">Total Cash</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => (
            <TableRow key={event.id}>
              <TableCell>{formatDate(event.activityDate)}</TableCell>
              <TableCell>
                <Badge variant={event.eventType === 'reversal' ? 'destructive' : 'outline'}>
                  {event.eventType}
                </Badge>
              </TableCell>
              <TableCell className="font-mono">{event.loanNumber}</TableCell>
              <TableCell>{event.borrowerName}</TableCell>
              <TableCell>{event.paymentNumber}</TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(event.principal)}</TableCell>
              <TableCell className="text-right font-mono font-semibold text-violet-600">{formatCurrency(event.interest + event.lateFee)}</TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(event.escrow)}</TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(event.total)}</TableCell>
              <TableCell>{event.status}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={5} className="font-bold">TOTAL ({events.length} events)</TableCell>
            <TableCell className="text-right font-mono font-bold">{formatCurrency(events.reduce((sum, event) => sum + event.principal, 0))}</TableCell>
            <TableCell className="text-right font-mono font-bold text-violet-600">{formatCurrency(events.reduce((sum, event) => sum + event.interest + event.lateFee, 0))}</TableCell>
            <TableCell className="text-right font-mono font-bold">{formatCurrency(events.reduce((sum, event) => sum + event.escrow, 0))}</TableCell>
            <TableCell className="text-right font-mono font-bold">{formatCurrency(events.reduce((sum, event) => sum + event.total, 0))}</TableCell>
            <TableCell />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}

async function exportToExcel(data: CompanyRevenueReportData, dateLabel: string, period: ReportPeriod) {
  const {
    bankedLoans: loans,
    brokeredLoans,
    servicingEvents,
    principalTieOuts
  } = data;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'LendWise';
  workbook.created = new Date();

  const boldFont: Partial<ExcelJS.Font> = { bold: true, size: 11 };
  const headerFont: Partial<ExcelJS.Font> = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
  const headerFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF333333' } };

  const totals = buildCompanyRevenueDisplayTotals(loans);
  const unifiedTotals = buildUnifiedCompanyRevenueTotals(loans, brokeredLoans, servicingEvents);

  const summary = workbook.addWorksheet('Summary');
  summary.getColumn(1).width = 30;
  summary.getColumn(2).width = 20;

  let rowIndex = 1;
  summary.getCell(rowIndex, 1).value = 'Company Revenue Report';
  summary.getCell(rowIndex, 1).font = { bold: true, size: 14 };
  rowIndex += 1;
  summary.getCell(rowIndex, 1).value = `Period: ${dateLabel}`;
  summary.getCell(rowIndex, 1).font = { size: 10, color: { argb: 'FF666666' } };
  rowIndex += 2;

  const summaryRows: Array<[string, number]> = [
    ['Banked Loans', loans.length],
    ['Brokered Loans', brokeredLoans.length],
    ['Servicing Cash Events', servicingEvents.length],
    ['Banked Residual Wires Received', unifiedTotals.bankedResidualWires],
    ['Brokered Gross Checks Received', unifiedTotals.brokeredGrossChecks],
    ['Servicing Mortgage-Payment Cash', unifiedTotals.servicingCashReceived],
    ['Corrected Banked Gross Profit Before Servicing', unifiedTotals.bankedContributionBeforeServicing],
    ['Brokered Company Revenue', unifiedTotals.brokeredCompanyRevenue],
    ['Servicing Interest Income by Receipt Date', unifiedTotals.servicingInterestIncome],
    ['Consolidated Company Income', unifiedTotals.consolidatedCompanyIncome],
    ['Purchase-Basis Servicing Interest (Control Only)', unifiedTotals.purchaseBasisServicingInterest],
    ['Banked Clearing / Pass-Through Wire Effect', totals.passThrough],
    ['Banked Outside-Wire Clearing Effect', totals.passThroughOutside]
  ];

  for (const [label, value] of summaryRows) {
    summary.getCell(rowIndex, 1).value = label;
    summary.getCell(rowIndex, 1).font = boldFont;
    summary.getCell(rowIndex, 2).value = value;
    if (typeof value === 'number' && !label.endsWith('Loans') && label !== 'Servicing Cash Events') {
      summary.getCell(rowIndex, 2).numFmt = '$#,##0.00';
    }
    rowIndex += 1;
  }

  rowIndex += 1;
  summary.getCell(rowIndex, 1).value = 'Accounting Note';
  summary.getCell(rowIndex, 1).font = boldFont;
  summary.getCell(rowIndex, 2).value = 'Servicing interest is recognized once, by actual receipt date. Purchase-basis servicing interest remains a non-additive control. Principal, escrow, reimbursables, shortages, haircut reserves, and other pass-throughs belong in balance-sheet clearing accounts.';
  summary.getCell(rowIndex, 2).alignment = { wrapText: true };
  summary.getRow(rowIndex).height = 42;
  summary.getColumn(2).width = 70;

  const instructions = workbook.addWorksheet('Instructions');
  instructions.columns = [
    { header: 'Area', key: 'area', width: 28 },
    { header: 'Instruction', key: 'instruction', width: 96 }
  ];
  instructions.getRow(1).eachCell((cell) => {
    cell.font = headerFont;
    cell.fill = headerFill;
  });
  [
    {
      area: 'Residual wires',
      instruction: 'Reconcile the Residual Wire Received column to bank deposits by loan number. This is the net wire back to LendWise from the warehouse settlement, not the investor payoff wire.'
    },
    {
      area: 'Recognition dates',
      instruction: 'Banked activity uses investor purchase date, brokered company revenue uses Broker Recon finalized date in Pacific time, and servicing income uses the actual payment-received date.'
    },
    {
      area: 'Servicing income timing',
      instruction: 'Purchase-basis servicing interest is retained only as an audit control. Consolidated company income removes it from the banked contribution and replaces it exactly once with receipt-date servicing interest.'
    },
    {
      area: 'Servicing principal',
      instruction: 'Borrower principal received is a positive clearing inflow. Investor principal settlement is a negative residual-wire clearing effect. Review the Principal Tie-Out tab for lifetime loan-level exceptions; neither leg is P&L income.'
    },
    {
      area: 'QuickBooks limitation',
      instruction: 'This workbook does not post or modify QuickBooks. Reimbursables posted as negative COGS must be reclassified by the accountant to a reimbursement or LO-payable clearing account.'
    },
    {
      area: 'Wire formula',
      instruction: 'Computed Residual Wire = Revenue in Wire - Expenses Deducted from Wire + Clearing / Pass-Through Wire Effect.'
    },
    {
      area: 'P&L revenue',
      instruction: 'Use True P&L Revenue for income-statement revenue. Do not include shortage recoups or other clearing recoveries as revenue.'
    },
    {
      area: 'P&L expenses',
      instruction: 'Use True P&L Expenses for income-statement expenses. This includes actual costs such as warehouse interest, funding fees, recon fees, underwriting fees, investor fees, lender credits, LO comp, and overrides.'
    },
    {
      area: 'Shortage funded / recouped',
      instruction: 'Post shortage funded and shortage recouped amounts to Warehouse / MOR Shortage Clearing. They should net toward zero over the loan lifecycle as funded shortages are recouped through sale proceeds.'
    },
    {
      area: 'Escrow deposits',
      instruction: 'Post escrow deposit pass-through activity to Escrow / Warehouse Clearing. It should not be treated as company operating expense.'
    },
    {
      area: 'Non-QM haircut reserve',
      instruction: 'Post haircut reserve activity to a balance sheet reserve or clearing account. It should not be treated as company operating expense while it is a reserve held in the settlement waterfall.'
    },
    {
      area: 'Government funding and reimbursables',
      instruction: 'Government funding pass-throughs and reimbursables are support/clearing items. Use the Clearing Activity tab to see gross amount and signed wire effect before posting.'
    },
    {
      area: 'FHA UFMIP',
      instruction: 'The premium retained at closing is an in-wire clearing inflow. The full HUD remittance is an outside-wire clearing outflow. Only their signed difference is company P&L; post both cash legs to FHA UFMIP / HUD Clearing.'
    },
    {
      area: 'Gross memo columns',
      instruction: 'Gross memo columns on the Accounting tab are support values only. They do not sum to Clearing Wire Effect; use signed wire-effect columns or the Clearing Activity tab to tie the residual wire.'
    },
    {
      area: 'Review items',
      instruction: 'Rows marked Review have a wire difference or P&L identity issue and should be investigated against the warehouse recon, purchase advice, and bank transaction before posting.'
    }
  ].forEach((row) => instructions.addRow(row));
  instructions.eachRow((row) => {
    row.alignment = { vertical: 'top', wrapText: true };
  });

  const detail = workbook.addWorksheet('Loan Detail');
  detail.columns = [
    { header: 'Loan #', key: 'loanNumber', width: 16 },
    { header: 'Borrower', key: 'borrower', width: 24 },
    { header: 'LO', key: 'lo', width: 20 },
    { header: 'Investor', key: 'investor', width: 24 },
    { header: 'Purchased', key: 'purchased', width: 14 },
    { header: 'Loan Amount', key: 'loanAmount', width: 16 },
    { header: 'Residual Wire Received', key: 'wire', width: 20 },
    { header: 'True P&L Revenue', key: 'revenue', width: 18 },
    { header: 'True P&L Expenses', key: 'expenses', width: 18 },
    { header: 'Clearing / Pass-Through', key: 'passThrough', width: 22 },
    { header: 'Outside-Wire Clearing', key: 'passThroughOutside', width: 22 },
    { header: 'Net Profit', key: 'netProfit', width: 16 },
    { header: 'Wire Check (R-E+P)', key: 'wireCheck', width: 18 },
    { header: 'Balanced', key: 'balanced', width: 10 }
  ];

  const detailHeaderRow = detail.getRow(1);
  detailHeaderRow.eachCell((cell) => {
    cell.font = headerFont;
    cell.fill = headerFill;
  });

  for (const loan of loans) {
    const decomposition = loan.decomposition;
    const accounting = loan.accounting;
    detail.addRow({
      loanNumber: loan.loanNumber,
      borrower: loan.borrowerName,
      lo: loan.loName,
      investor: loan.investorName,
      purchased: loan.investorPurchaseDate ? formatDate(loan.investorPurchaseDate) : '',
      loanAmount: loan.loanAmount,
      wire: accounting.wireReceived,
      revenue: accounting.revenue,
      expenses: accounting.expenses,
      passThrough: accounting.passThrough,
      passThroughOutside: accounting.passThroughOutside,
      netProfit: accounting.netProfit,
      wireCheck: decomposition.computedWire,
      balanced: accounting.isAuditBalanced ? 'Yes' : 'NO'
    });
  }

  for (let columnIndex = 6; columnIndex <= 13; columnIndex += 1) {
    detail.getColumn(columnIndex).numFmt = '$#,##0.00';
    detail.getColumn(columnIndex).alignment = { horizontal: 'right' };
  }

  const detailTotalsRow = detail.addRow({
    loanNumber: 'TOTAL',
    loanAmount: loans.reduce((sum, loan) => sum + loan.loanAmount, 0),
    wire: totals.wire,
    revenue: totals.revenue,
    expenses: totals.expenses,
    passThrough: totals.passThrough,
    passThroughOutside: totals.passThroughOutside,
    netProfit: totals.netProfit,
    wireCheck: loans.reduce((sum, loan) => sum + loan.decomposition.computedWire, 0)
  });
  detailTotalsRow.font = { bold: true };

  const wireRecon = workbook.addWorksheet('Wire Reconciliation');
  wireRecon.columns = [
    { header: 'Loan #', key: 'loanNumber', width: 16 },
    { header: 'Borrower', key: 'borrower', width: 24 },
    { header: 'Purchased', key: 'purchased', width: 14 },
    { header: 'Residual Wire Received', key: 'wireReceived', width: 22 },
    { header: 'Revenue in Wire', key: 'revenueInWire', width: 18 },
    { header: 'Less Expenses in Wire', key: 'expenseInWire', width: 22 },
    { header: 'Clearing Wire Effect', key: 'clearingWireEffect', width: 22 },
    { header: 'Computed Residual Wire', key: 'computedWire', width: 22 },
    { header: 'Difference', key: 'difference', width: 16 },
    { header: 'Balanced', key: 'balanced', width: 12 },
    { header: 'Formula', key: 'formula', width: 62 }
  ];
  wireRecon.getRow(1).eachCell((cell) => {
    cell.font = headerFont;
    cell.fill = headerFill;
  });
  for (const loan of loans) {
    wireRecon.addRow({
      loanNumber: loan.loanNumber,
      borrower: loan.borrowerName,
      purchased: loan.investorPurchaseDate ? formatDate(loan.investorPurchaseDate) : '',
      wireReceived: loan.accounting.wireReceived,
      revenueInWire: loan.accounting.revenueInWire,
      expenseInWire: loan.accounting.expenseInWire,
      clearingWireEffect: loan.accounting.passThrough,
      computedWire: loan.decomposition.computedWire,
      difference: loan.accounting.wireReceived - loan.decomposition.computedWire,
      balanced: loan.accounting.isAuditBalanced ? 'Yes' : 'Review',
      formula: 'Revenue in Wire - Expenses in Wire + Clearing Wire Effect'
    });
  }
  const wireTotalsRow = wireRecon.addRow({
    loanNumber: 'TOTAL',
    wireReceived: totals.wire,
    revenueInWire: totals.revenueInWire,
    expenseInWire: totals.expenseInWire,
    clearingWireEffect: totals.passThrough,
    computedWire: loans.reduce((sum, loan) => sum + loan.decomposition.computedWire, 0),
    difference: loans.reduce((sum, loan) => sum + (loan.accounting.wireReceived - loan.decomposition.computedWire), 0)
  });
  wireTotalsRow.font = { bold: true };
  for (let columnIndex = 4; columnIndex <= 9; columnIndex += 1) {
    wireRecon.getColumn(columnIndex).numFmt = '$#,##0.00;[Red]($#,##0.00)';
    wireRecon.getColumn(columnIndex).alignment = { horizontal: 'right' };
  }
  wireRecon.views = [{ state: 'frozen', ySplit: 1 }];

  const items = workbook.addWorksheet('Line Items');
  const bucketOrder: Array<{ bucket: string; label: string; color: string }> = [
    { bucket: 'revenue_in_wire', label: 'REVENUE (In Wire)', color: 'FF22A34A' },
    { bucket: 'revenue_outside', label: 'REVENUE (Outside Wire)', color: 'FF22A34A' },
    { bucket: 'expense_in_wire', label: 'EXPENSES (Deducted from Wire)', color: 'FFDC2626' },
    { bucket: 'expense_outside', label: 'EXPENSES (Paid Separately)', color: 'FFDC2626' },
    { bucket: 'pass_through_in_wire', label: 'PASS-THROUGH (In Wire)', color: 'FF2563EB' },
    { bucket: 'pass_through_outside', label: 'PASS-THROUGH (Outside Wire)', color: 'FF0F766E' }
  ];

  const bucketDescriptions = new Map<string, string[]>();
  for (const { bucket } of bucketOrder) {
    const descriptions = new Set<string>();
    for (const loan of loans) {
      for (const item of loan.decomposition.items) {
        if (item.bucket === bucket) {
          descriptions.add(item.description);
        }
      }
    }
    bucketDescriptions.set(bucket, [...descriptions]);
  }

  const fixedColumns = [
    { header: 'Loan #', width: 16 },
    { header: 'Borrower', width: 22 },
    { header: 'Wire Received', width: 16 }
  ];

  const columnMap = new Map<string, number>();
  let columnIndex = fixedColumns.length + 1;

  const row1 = items.getRow(1);
  const row2 = items.getRow(2);

  for (let fixedIndex = 0; fixedIndex < fixedColumns.length; fixedIndex += 1) {
    items.getColumn(fixedIndex + 1).width = fixedColumns[fixedIndex].width;
  }

  row1.getCell(1).value = 'Loan #';
  row1.getCell(2).value = 'Borrower';
  row1.getCell(3).value = 'Wire Received';
  row2.getCell(1).value = '';
  row2.getCell(2).value = '';
  row2.getCell(3).value = '';

  for (const { bucket, label, color } of bucketOrder) {
    const descriptions = bucketDescriptions.get(bucket) || [];
    if (descriptions.length === 0) {
      continue;
    }

    const startColumn = columnIndex;
    for (const description of descriptions) {
      columnMap.set(`${bucket}::${description}`, columnIndex);
      row2.getCell(columnIndex).value = description;
      row2.getCell(columnIndex).font = { size: 9 };
      items.getColumn(columnIndex).width = 16;
      items.getColumn(columnIndex).numFmt = '$#,##0.00';
      items.getColumn(columnIndex).alignment = { horizontal: 'right' };
      columnIndex += 1;
    }

    const totalLabel = `Total ${label.split('(')[0].trim()}`;
    columnMap.set(`${bucket}::__total__`, columnIndex);
    row2.getCell(columnIndex).value = totalLabel;
    row2.getCell(columnIndex).font = { bold: true, size: 9 };
    items.getColumn(columnIndex).width = 18;
    items.getColumn(columnIndex).numFmt = '$#,##0.00';
    items.getColumn(columnIndex).alignment = { horizontal: 'right' };
    columnIndex += 1;

    const endColumn = columnIndex - 1;
    if (endColumn > startColumn) {
      items.mergeCells(1, startColumn, 1, endColumn);
    }
    row1.getCell(startColumn).value = label;
    row1.getCell(startColumn).alignment = { horizontal: 'center' };
    for (let fillColumn = startColumn; fillColumn <= endColumn; fillColumn += 1) {
      row1.getCell(fillColumn).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
      row1.getCell(fillColumn).font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
    }
  }

  const netProfitColumn = columnIndex;
  row1.getCell(netProfitColumn).value = 'NET PROFIT';
  row1.getCell(netProfitColumn).font = { bold: true, size: 10 };
  row2.getCell(netProfitColumn).value = 'Rev - Exp';
  items.getColumn(netProfitColumn).width = 16;
  items.getColumn(netProfitColumn).numFmt = '$#,##0.00';
  columnIndex += 1;

  const wireCheckColumn = columnIndex;
  row1.getCell(wireCheckColumn).value = 'WIRE CHECK';
  row1.getCell(wireCheckColumn).font = { bold: true, size: 10 };
  row2.getCell(wireCheckColumn).value = 'Rev-Exp+PT';
  items.getColumn(wireCheckColumn).width = 16;
  items.getColumn(wireCheckColumn).numFmt = '$#,##0.00';

  for (let fixedColumn = 1; fixedColumn <= 3; fixedColumn += 1) {
    row1.getCell(fixedColumn).font = headerFont;
    row1.getCell(fixedColumn).fill = headerFill;
    row2.getCell(fixedColumn).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } };
  }
  row2.getCell(netProfitColumn).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } };
  row2.getCell(wireCheckColumn).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } };

  for (const loan of loans) {
    const decomposition = loan.decomposition;
    const accounting = loan.accounting;
    const dataRow = items.addRow([]);
    dataRow.getCell(1).value = loan.loanNumber;
    dataRow.getCell(2).value = loan.borrowerName;
    dataRow.getCell(3).value = accounting.wireReceived;
    dataRow.getCell(3).numFmt = '$#,##0.00';

    const bucketTotals = new Map<string, number>();

    for (const item of decomposition.items) {
      const isOutside =
        item.bucket === 'expense_outside'
        || item.bucket === 'revenue_outside'
        || item.bucket === 'pass_through_outside';
      const cellValue = isOutside
        ? (
          item.bucket === 'expense_outside'
            ? -item.amount
            : item.bucket === 'pass_through_outside'
              ? item.wireEffect
              : item.amount
        )
        : item.wireEffect;

      const detailKey = `${item.bucket}::${item.description}`;
      const detailColumn = columnMap.get(detailKey);
      if (detailColumn) {
        dataRow.getCell(detailColumn).value = cellValue;
      }

      const totalKey = `${item.bucket}::__total__`;
      bucketTotals.set(totalKey, (bucketTotals.get(totalKey) || 0) + cellValue);
    }

    for (const [bucketKey, total] of bucketTotals) {
      const totalColumn = columnMap.get(bucketKey);
      if (totalColumn) {
        dataRow.getCell(totalColumn).value = total;
        dataRow.getCell(totalColumn).font = { bold: true };
      }
    }

    dataRow.getCell(netProfitColumn).value = accounting.netProfit;
    dataRow.getCell(netProfitColumn).font = { bold: true };
    dataRow.getCell(wireCheckColumn).value = decomposition.computedWire;
  }

  items.views = [{ state: 'frozen', xSplit: 3, ySplit: 2 }];

  const clearingActivity = workbook.addWorksheet('Clearing Activity');
  clearingActivity.columns = [
    { header: 'Loan #', key: 'loanNumber', width: 16 },
    { header: 'Borrower', key: 'borrower', width: 24 },
    { header: 'Purchased', key: 'purchased', width: 14 },
    { header: 'Residual Wire Received', key: 'wireReceived', width: 22 },
    { header: 'Clearing Item', key: 'description', width: 34 },
    { header: 'Gross Amount', key: 'amount', width: 16 },
    { header: 'Wire Effect', key: 'wireEffect', width: 16 },
    { header: 'Suggested Account', key: 'account', width: 34 },
    { header: 'Suggested Treatment', key: 'treatment', width: 42 }
  ];
  clearingActivity.getRow(1).eachCell((cell) => {
    cell.font = headerFont;
    cell.fill = headerFill;
  });
  for (const loan of loans) {
    for (const item of loan.decomposition.items) {
      if (
        item.bucket !== 'pass_through_in_wire'
        && item.bucket !== 'pass_through_outside'
      ) {
        continue;
      }
      clearingActivity.addRow({
        loanNumber: loan.loanNumber,
        borrower: loan.borrowerName,
        purchased: loan.investorPurchaseDate ? formatDate(loan.investorPurchaseDate) : '',
        wireReceived: loan.accounting.wireReceived,
        description: item.description,
        amount: item.amount,
        wireEffect: item.wireEffect,
        account: getClearingAccount(item),
        treatment: item.clearingType === 'fha_ufmip_hud_remittance'
          ? 'Balance sheet HUD clearing outflow; only the premium-to-remittance variance is P&L'
          : 'Balance sheet clearing / pass-through, not P&L expense'
      });
    }
  }
  const clearingTotals = loans.reduce(
    (sum, loan) => sum + loan.accounting.passThrough + loan.accounting.passThroughOutside,
    0
  );
  const clearingTotalsRow = clearingActivity.addRow({
    loanNumber: 'TOTAL',
    wireEffect: clearingTotals
  });
  clearingTotalsRow.font = { bold: true };
  for (let columnIndex = 4; columnIndex <= 7; columnIndex += 1) {
    clearingActivity.getColumn(columnIndex).numFmt = '$#,##0.00;[Red]($#,##0.00)';
    clearingActivity.getColumn(columnIndex).alignment = { horizontal: 'right' };
  }
  clearingActivity.views = [{ state: 'frozen', ySplit: 1 }];

  const accounting = workbook.addWorksheet('Accounting');
  const accountingRows = buildAccountingExportRows(loans);

  accounting.columns = [
    { header: 'Loan #', key: 'loanNumber', width: 16 },
    { header: 'Borrower', key: 'borrowerName', width: 24 },
    { header: 'LO', key: 'loanOfficer', width: 20 },
    { header: 'Investor', key: 'investorName', width: 24 },
    { header: 'Purchased', key: 'investorPurchaseDate', width: 14 },
    { header: 'Loan Amount', key: 'loanAmount', width: 16 },
    { header: 'Residual Wire Received', key: 'wireReceived', width: 22 },
    { header: 'Computed Residual Wire', key: 'computedWire', width: 22 },
    { header: 'Wire Difference', key: 'wireDifference', width: 16 },
    { header: 'True P&L Revenue', key: 'revenue', width: 18 },
    { header: 'True P&L Expenses', key: 'expenses', width: 18 },
    { header: 'Profit', key: 'netProfit', width: 16 },
    { header: 'Clearing Wire Effect', key: 'clearingWireEffect', width: 22 },
    { header: 'Shortage Funded Gross Memo', key: 'shortageFundedGrossMemo', width: 28 },
    { header: 'Shortage Funded Wire Effect', key: 'shortageFundedWireEffect', width: 28 },
    { header: 'Shortage Recouped Gross Memo', key: 'shortageRecoupedGrossMemo', width: 30 },
    { header: 'Shortage Recouped Wire Effect', key: 'shortageRecoupedWireEffect', width: 30 },
    { header: 'Net Shortage Gross Memo', key: 'netShortageGrossMemo', width: 24 },
    { header: 'Net Shortage Wire Effect', key: 'netShortageWireEffect', width: 26 },
    { header: 'Escrow Clearing Wire Effect', key: 'escrowClearingWireEffect', width: 26 },
    { header: 'Non-QM Haircut Reserve Wire Effect', key: 'nonQmHaircutReserveWireEffect', width: 34 },
    { header: 'Government Funding Gross Memo', key: 'governmentFundingGrossMemo', width: 30 },
    { header: 'Government Funding Wire Effect', key: 'governmentFundingWireEffect', width: 30 },
    { header: 'Buydown Pass-Through Gross Memo', key: 'buydownPassThroughGrossMemo', width: 34 },
    { header: 'Buydown Pass-Through Wire Effect', key: 'buydownPassThroughWireEffect', width: 34 },
    { header: 'Reimbursable Clearing Wire Effect', key: 'reimbursableClearingWireEffect', width: 32 },
    { header: 'Investor Principal Settlement Wire Effect', key: 'servicingPrincipalSettlementWireEffect', width: 36 },
    { header: 'Other Clearing Wire Effect', key: 'otherClearingWireEffect', width: 26 },
    { header: 'FHA UFMIP Premium Gross Memo', key: 'fhaUfmipPremiumGrossMemo', width: 30 },
    { header: 'FHA UFMIP Premium Wire Effect', key: 'fhaUfmipPremiumWireEffect', width: 30 },
    { header: 'FHA UFMIP HUD Remittance', key: 'fhaUfmipHudRemittance', width: 28 },
    { header: 'FHA UFMIP Outside Clearing Effect', key: 'fhaUfmipOutsideClearingEffect', width: 34 },
    { header: 'FHA UFMIP Company Variance', key: 'fhaUfmipCompanyVariance', width: 30 },
    { header: 'Balanced', key: 'balanced', width: 12 },
    { header: 'Suggested Treatment', key: 'suggestedTreatment', width: 46 }
  ];

  accounting.mergeCells(1, 1, 1, accounting.columns.length);
  accounting.getCell('A1').value = 'Company Revenue - Accounting';
  accounting.getCell('A1').font = { bold: true, size: 14 };
  accounting.getCell('A2').value = `Period: ${dateLabel}`;
  accounting.getCell('A2').font = { size: 10, color: { argb: 'FF666666' } };
  accounting.mergeCells(3, 1, 3, accounting.columns.length);
  accounting.getCell('A3').value = 'Note: gross memo columns are support values only and do not sum to Clearing Wire Effect. Use signed wire-effect columns or the Clearing Activity tab to reconcile residual wires.';
  accounting.getCell('A3').font = { italic: true, color: { argb: 'FF666666' } };
  accounting.getCell('A3').alignment = { wrapText: true };

  const accountingHeaderRow = accounting.getRow(4);
  accountingHeaderRow.values = accounting.columns.map((column) => String(column.header ?? ''));
  accountingHeaderRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF333333' } };
  });

  accountingRows.forEach((exportRow) => {
    accounting.addRow({
      ...exportRow,
      investorPurchaseDate: exportRow.investorPurchaseDate ? formatDate(exportRow.investorPurchaseDate) : ''
    });
  });

  for (let accountingRowIndex = 5; accountingRowIndex < 5 + accountingRows.length; accountingRowIndex += 1) {
    for (let accountingColumnIndex = 6; accountingColumnIndex <= 33; accountingColumnIndex += 1) {
      accounting.getRow(accountingRowIndex).getCell(accountingColumnIndex).numFmt = '$#,##0.00';
      accounting.getRow(accountingRowIndex).getCell(accountingColumnIndex).alignment = { horizontal: 'right' };
    }
  }

  const accountingTotalsRow = accounting.addRow({
    loanNumber: `TOTAL (${accountingRows.length} loans)`,
    loanAmount: accountingRows.reduce((sum, exportRow) => sum + exportRow.loanAmount, 0),
    wireReceived: accountingRows.reduce((sum, exportRow) => sum + exportRow.wireReceived, 0),
    computedWire: accountingRows.reduce((sum, exportRow) => sum + exportRow.computedWire, 0),
    wireDifference: accountingRows.reduce((sum, exportRow) => sum + exportRow.wireDifference, 0),
    revenue: accountingRows.reduce((sum, exportRow) => sum + exportRow.revenue, 0),
    expenses: accountingRows.reduce((sum, exportRow) => sum + exportRow.expenses, 0),
    netProfit: accountingRows.reduce((sum, exportRow) => sum + exportRow.netProfit, 0),
    clearingWireEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.clearingWireEffect, 0),
    shortageFundedGrossMemo: accountingRows.reduce((sum, exportRow) => sum + exportRow.shortageFundedGrossMemo, 0),
    shortageFundedWireEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.shortageFundedWireEffect, 0),
    shortageRecoupedGrossMemo: accountingRows.reduce((sum, exportRow) => sum + exportRow.shortageRecoupedGrossMemo, 0),
    shortageRecoupedWireEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.shortageRecoupedWireEffect, 0),
    netShortageGrossMemo: accountingRows.reduce((sum, exportRow) => sum + exportRow.netShortageGrossMemo, 0),
    netShortageWireEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.netShortageWireEffect, 0),
    escrowClearingWireEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.escrowClearingWireEffect, 0),
    nonQmHaircutReserveWireEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.nonQmHaircutReserveWireEffect, 0),
    governmentFundingGrossMemo: accountingRows.reduce((sum, exportRow) => sum + exportRow.governmentFundingGrossMemo, 0),
    governmentFundingWireEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.governmentFundingWireEffect, 0),
    buydownPassThroughGrossMemo: accountingRows.reduce((sum, exportRow) => sum + exportRow.buydownPassThroughGrossMemo, 0),
    buydownPassThroughWireEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.buydownPassThroughWireEffect, 0),
    reimbursableClearingWireEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.reimbursableClearingWireEffect, 0),
    servicingPrincipalSettlementWireEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.servicingPrincipalSettlementWireEffect, 0),
    otherClearingWireEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.otherClearingWireEffect, 0),
    fhaUfmipPremiumGrossMemo: accountingRows.reduce((sum, exportRow) => sum + exportRow.fhaUfmipPremiumGrossMemo, 0),
    fhaUfmipPremiumWireEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.fhaUfmipPremiumWireEffect, 0),
    fhaUfmipHudRemittance: accountingRows.reduce((sum, exportRow) => sum + exportRow.fhaUfmipHudRemittance, 0),
    fhaUfmipOutsideClearingEffect: accountingRows.reduce((sum, exportRow) => sum + exportRow.fhaUfmipOutsideClearingEffect, 0),
    fhaUfmipCompanyVariance: accountingRows.reduce((sum, exportRow) => sum + exportRow.fhaUfmipCompanyVariance, 0)
  });
  accountingTotalsRow.font = { bold: true };

  const accountingTotalsRowIndex = accountingTotalsRow.number;
  for (let accountingColumnIndex = 6; accountingColumnIndex <= 33; accountingColumnIndex += 1) {
    accounting.getRow(accountingTotalsRowIndex).getCell(accountingColumnIndex).numFmt = '$#,##0.00';
    accounting.getRow(accountingTotalsRowIndex).getCell(accountingColumnIndex).alignment = { horizontal: 'right' };
  }

  accounting.views = [{ state: 'frozen', ySplit: 4 }];

  const journalSupport = workbook.addWorksheet('Journal Support');
  journalSupport.columns = [
    { header: 'Support Line', key: 'line', width: 38 },
    { header: 'Amount', key: 'amount', width: 18 },
    { header: 'Suggested Treatment', key: 'treatment', width: 72 }
  ];
  journalSupport.getRow(1).eachCell((cell) => {
    cell.font = headerFont;
    cell.fill = headerFill;
  });
  const supportTotals = {
    trueRevenue: accountingRows.reduce((sum, row) => sum + row.revenue, 0),
    trueExpenses: accountingRows.reduce((sum, row) => sum + row.expenses, 0),
    netProfit: accountingRows.reduce((sum, row) => sum + row.netProfit, 0),
    residualWires: accountingRows.reduce((sum, row) => sum + row.wireReceived, 0),
    shortageFundedGrossMemo: accountingRows.reduce((sum, row) => sum + row.shortageFundedGrossMemo, 0),
    shortageFundedWireEffect: accountingRows.reduce((sum, row) => sum + row.shortageFundedWireEffect, 0),
    shortageRecoupedGrossMemo: accountingRows.reduce((sum, row) => sum + row.shortageRecoupedGrossMemo, 0),
    shortageRecoupedWireEffect: accountingRows.reduce((sum, row) => sum + row.shortageRecoupedWireEffect, 0),
    netShortageGrossMemo: accountingRows.reduce((sum, row) => sum + row.netShortageGrossMemo, 0),
    netShortageWireEffect: accountingRows.reduce((sum, row) => sum + row.netShortageWireEffect, 0),
    escrowWireEffect: accountingRows.reduce((sum, row) => sum + row.escrowClearingWireEffect, 0),
    nonQmHaircutReserveWireEffect: accountingRows.reduce((sum, row) => sum + row.nonQmHaircutReserveWireEffect, 0),
    governmentFundingGrossMemo: accountingRows.reduce((sum, row) => sum + row.governmentFundingGrossMemo, 0),
    governmentFundingWireEffect: accountingRows.reduce((sum, row) => sum + row.governmentFundingWireEffect, 0),
    buydownPassThroughGrossMemo: accountingRows.reduce((sum, row) => sum + row.buydownPassThroughGrossMemo, 0),
    buydownPassThroughWireEffect: accountingRows.reduce((sum, row) => sum + row.buydownPassThroughWireEffect, 0),
    reimbursableClearingWireEffect: accountingRows.reduce((sum, row) => sum + row.reimbursableClearingWireEffect, 0),
    servicingPrincipalSettlementWireEffect: accountingRows.reduce((sum, row) => sum + row.servicingPrincipalSettlementWireEffect, 0),
    otherClearingWireEffect: accountingRows.reduce((sum, row) => sum + row.otherClearingWireEffect, 0),
    fhaUfmipPremiumGrossMemo: accountingRows.reduce((sum, row) => sum + row.fhaUfmipPremiumGrossMemo, 0),
    fhaUfmipPremiumWireEffect: accountingRows.reduce((sum, row) => sum + row.fhaUfmipPremiumWireEffect, 0),
    fhaUfmipHudRemittance: accountingRows.reduce((sum, row) => sum + row.fhaUfmipHudRemittance, 0),
    fhaUfmipOutsideClearingEffect: accountingRows.reduce((sum, row) => sum + row.fhaUfmipOutsideClearingEffect, 0),
    fhaUfmipCompanyVariance: accountingRows.reduce((sum, row) => sum + row.fhaUfmipCompanyVariance, 0),
    totalClearingWireEffect: accountingRows.reduce((sum, row) => sum + row.clearingWireEffect, 0),
    wireDifference: accountingRows.reduce((sum, row) => sum + row.wireDifference, 0)
  };
  [
    { line: 'Residual wires received', amount: supportTotals.residualWires, treatment: 'Tie to bank deposits by loan number.' },
    { line: 'True P&L revenue', amount: supportTotals.trueRevenue, treatment: 'Income statement revenue.' },
    { line: 'True P&L expenses', amount: supportTotals.trueExpenses, treatment: 'Income statement expenses.' },
    { line: 'Net profit', amount: supportTotals.netProfit, treatment: 'True P&L revenue less true P&L expenses.' },
    { line: 'Shortage funded gross memo', amount: supportTotals.shortageFundedGrossMemo, treatment: 'Gross support only. Signed wire effect is shown separately.' },
    { line: 'Shortage funded wire effect', amount: supportTotals.shortageFundedWireEffect, treatment: 'Clearing wire effect; positive funded shortages reduce the residual wire.' },
    { line: 'Shortage recouped gross memo', amount: supportTotals.shortageRecoupedGrossMemo, treatment: 'Gross support only. Signed wire effect is shown separately.' },
    { line: 'Shortage recouped wire effect', amount: supportTotals.shortageRecoupedWireEffect, treatment: 'Clearing recovery; not P&L revenue.' },
    { line: 'Net shortage gross memo', amount: supportTotals.netShortageGrossMemo, treatment: 'Funded gross less recouped gross; support value only.' },
    { line: 'Net shortage wire effect', amount: supportTotals.netShortageWireEffect, treatment: 'Signed shortage impact in residual wire reconciliation.' },
    { line: 'Escrow clearing wire effect', amount: supportTotals.escrowWireEffect, treatment: 'Post to Escrow / Warehouse Clearing, not P&L.' },
    { line: 'Non-QM haircut reserve wire effect', amount: supportTotals.nonQmHaircutReserveWireEffect, treatment: 'Post to balance sheet reserve/clearing, not P&L.' },
    { line: 'Government funding gross memo', amount: supportTotals.governmentFundingGrossMemo, treatment: 'Pass-through support only; normally zero wire effect.' },
    { line: 'Government funding wire effect', amount: supportTotals.governmentFundingWireEffect, treatment: 'Signed residual-wire effect for government funding pass-throughs.' },
    { line: 'Buydown pass-through gross memo', amount: supportTotals.buydownPassThroughGrossMemo, treatment: 'Temporary subsidy support only; not P&L revenue or expense.' },
    { line: 'Buydown pass-through wire effect', amount: supportTotals.buydownPassThroughWireEffect, treatment: 'Signed residual-wire effect for temporary subsidy pass-throughs.' },
    { line: 'Reimbursable clearing wire effect', amount: supportTotals.reimbursableClearingWireEffect, treatment: 'Cash collected in settlement and payable/reimbursable outside P&L.' },
    { line: 'Investor principal settlement wire effect', amount: supportTotals.servicingPrincipalSettlementWireEffect, treatment: 'Principal already collected from the borrower that reduced the investor purchase wire; post to servicing principal clearing.' },
    { line: 'Other clearing wire effect', amount: supportTotals.otherClearingWireEffect, treatment: 'Review detail on Clearing Activity tab.' },
    { line: 'FHA UFMIP premium gross memo', amount: supportTotals.fhaUfmipPremiumGrossMemo, treatment: 'Premium collected at closing; balance-sheet support value.' },
    { line: 'FHA UFMIP premium wire effect', amount: supportTotals.fhaUfmipPremiumWireEffect, treatment: 'Positive residual-wire effect; post to FHA UFMIP / HUD Clearing.' },
    { line: 'FHA UFMIP HUD remittance', amount: supportTotals.fhaUfmipHudRemittance, treatment: 'Full cash payment remitted outside the residual wire; balance-sheet support value.' },
    { line: 'FHA UFMIP outside clearing effect', amount: supportTotals.fhaUfmipOutsideClearingEffect, treatment: 'Negative outside-wire cash leg; post to FHA UFMIP / HUD Clearing.' },
    { line: 'FHA UFMIP company variance', amount: supportTotals.fhaUfmipCompanyVariance, treatment: 'Only this signed premium-to-remittance difference is company P&L.' },
    { line: 'Total clearing wire effect', amount: supportTotals.totalClearingWireEffect, treatment: 'Part of residual wire reconciliation; not an expense total.' },
    { line: 'Total wire difference', amount: supportTotals.wireDifference, treatment: 'Should be near zero. Non-zero amount requires loan-level review before posting.' }
  ].forEach((row) => journalSupport.addRow(row));
  journalSupport.getColumn('amount').numFmt = '$#,##0.00;[Red]($#,##0.00)';
  journalSupport.getColumn('amount').alignment = { horizontal: 'right' };
  journalSupport.eachRow((row) => {
    row.alignment = { vertical: 'top', wrapText: true };
  });

  const accountingBridge = workbook.addWorksheet('Accounting Bridge');
  accountingBridge.columns = [
    { header: 'Bridge Line', key: 'line', width: 48 },
    { header: 'Amount', key: 'amount', width: 20 },
    { header: 'Explanation', key: 'explanation', width: 86 }
  ];
  accountingBridge.getRow(1).eachCell((cell) => {
    cell.font = headerFont;
    cell.fill = headerFill;
  });
  const reimbursableClearing = accountingRows.reduce(
    (sum, row) => sum + row.reimbursableClearingWireEffect,
    0
  );
  const correctedBankedGrossProfit = unifiedTotals.bankedResidualWires - totals.passThrough;
  [
    {
      line: 'Actual banked residual wires',
      amount: unifiedTotals.bankedResidualWires,
      explanation: 'Tie to warehouse residual-wire bank deposits.'
    },
    {
      line: 'Less: net balance-sheet clearing contained in wires',
      amount: -totals.passThrough,
      explanation: 'Removes reimbursables, shortage, escrow, haircut, servicing-principal settlement, and other pass-through activity.'
    },
    {
      line: 'Corrected banked Gross Profit',
      amount: correctedBankedGrossProfit,
      explanation: 'Gross Profit from the banked wire bridge. This is not Net Income.'
    },
    {
      line: 'Reimbursables requiring QuickBooks reclassification',
      amount: reimbursableClearing,
      explanation: 'If posted as negative COGS, reclass to reimbursement / LO-payable clearing. This workbook does not post the adjustment.'
    },
    {
      line: 'Purchase-basis servicing interest removed from consolidated recognition',
      amount: -unifiedTotals.purchaseBasisServicingInterest,
      explanation: 'Timing reclass only; prevents the same servicing interest from being recognized by both purchase date and receipt date.'
    },
    {
      line: 'Receipt-date servicing interest recognized',
      amount: unifiedTotals.servicingInterestIncome,
      explanation: 'Actual interest and late-fee cash events in the selected receipt-date period.'
    },
    {
      line: 'Brokered company revenue recognized',
      amount: unifiedTotals.brokeredCompanyRevenue,
      explanation: 'Primary Broker Recon rows recognized by finalized date.'
    },
    {
      line: 'Consolidated company income',
      amount: unifiedTotals.consolidatedCompanyIncome,
      explanation: 'Banked contribution before servicing + brokered company revenue + receipt-date servicing income.'
    }
  ].forEach((row) => accountingBridge.addRow(row));
  accountingBridge.getColumn('amount').numFmt = '$#,##0.00;[Red]($#,##0.00)';
  accountingBridge.eachRow((row) => {
    row.alignment = { vertical: 'top', wrapText: true };
  });

  const brokered = workbook.addWorksheet('Brokered Income');
  brokered.columns = [
    { header: 'Finalized', key: 'finalizedAt', width: 20 },
    { header: 'Funded', key: 'fundDate', width: 14 },
    { header: 'Loan #', key: 'loanNumber', width: 16 },
    { header: 'Borrower', key: 'borrowerName', width: 24 },
    { header: 'LO', key: 'loanOfficerName', width: 22 },
    { header: 'Lender', key: 'lenderName', width: 24 },
    { header: 'Loan Amount', key: 'loanAmount', width: 16 },
    { header: 'Gross Check', key: 'grossCheckReceived', width: 16 },
    { header: 'LO Reimbursements', key: 'loReimbursements', width: 18 },
    { header: 'Processing Fee', key: 'processingFee', width: 16 },
    { header: 'Company Commission', key: 'companyCommissionBase', width: 20 },
    { header: 'E&O', key: 'eAndO', width: 12 },
    { header: 'LO Gross Payout', key: 'loGrossPayout', width: 18 },
    { header: 'LO Net Payout', key: 'loNetPayout', width: 18 },
    { header: 'Mentor Gross Payout', key: 'mentorGrossPayout', width: 20 },
    { header: 'Mentor Net Payout', key: 'mentorNetPayout', width: 20 },
    { header: 'Company Revenue', key: 'companyRevenue', width: 18 },
    { header: 'Status', key: 'status', width: 12 }
  ];
  brokered.getRow(1).eachCell((cell) => {
    cell.font = headerFont;
    cell.fill = headerFill;
  });
  brokeredLoans.forEach((loan) => brokered.addRow({
    ...loan,
    finalizedAt: new Date(loan.finalizedAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }),
    fundDate: loan.fundDate ? formatDate(loan.fundDate) : ''
  }));
  for (let brokeredColumn = 7; brokeredColumn <= 17; brokeredColumn += 1) {
    brokered.getColumn(brokeredColumn).numFmt = '$#,##0.00;[Red]($#,##0.00)';
  }
  const brokeredTotal = brokered.addRow({
    loanNumber: `TOTAL (${brokeredLoans.length} loans)`,
    loanAmount: brokeredLoans.reduce((sum, loan) => sum + loan.loanAmount, 0),
    grossCheckReceived: unifiedTotals.brokeredGrossChecks,
    companyRevenue: unifiedTotals.brokeredCompanyRevenue
  });
  brokeredTotal.font = { bold: true };
  brokered.views = [{ state: 'frozen', ySplit: 1 }];

  const servicing = workbook.addWorksheet('Servicing Income');
  servicing.columns = [
    { header: 'Activity Date', key: 'activityDate', width: 16 },
    { header: 'Event', key: 'eventType', width: 12 },
    { header: 'Due Date', key: 'dueDate', width: 14 },
    { header: 'Loan #', key: 'loanNumber', width: 16 },
    { header: 'Borrower', key: 'borrowerName', width: 24 },
    { header: 'LO', key: 'loanOfficerName', width: 22 },
    { header: 'Investor', key: 'investorName', width: 24 },
    { header: 'Payment #', key: 'paymentNumber', width: 12 },
    { header: 'Principal Clearing', key: 'principal', width: 18 },
    { header: 'Interest Income', key: 'interest', width: 18 },
    { header: 'Escrow Clearing', key: 'escrow', width: 18 },
    { header: 'Late Fee Income', key: 'lateFee', width: 18 },
    { header: 'Total Cash', key: 'total', width: 16 },
    { header: 'Method', key: 'paymentMethod', width: 16 },
    { header: 'Reference', key: 'referenceNumber', width: 18 },
    { header: 'Status', key: 'status', width: 20 },
    { header: 'Reversal Reason', key: 'reversalReason', width: 22 }
  ];
  servicing.getRow(1).eachCell((cell) => {
    cell.font = headerFont;
    cell.fill = headerFill;
  });
  servicingEvents.forEach((event) => servicing.addRow({
    ...event,
    activityDate: formatDate(event.activityDate),
    dueDate: formatDate(event.dueDate)
  }));
  for (let servicingColumn = 9; servicingColumn <= 13; servicingColumn += 1) {
    servicing.getColumn(servicingColumn).numFmt = '$#,##0.00;[Red]($#,##0.00)';
  }
  const servicingTotal = servicing.addRow({
    loanNumber: `TOTAL (${servicingEvents.length} events)`,
    principal: unifiedTotals.servicingPrincipalReceived,
    interest: servicingEvents.reduce((sum, event) => sum + event.interest, 0),
    escrow: unifiedTotals.servicingEscrowReceived,
    lateFee: servicingEvents.reduce((sum, event) => sum + event.lateFee, 0),
    total: unifiedTotals.servicingCashReceived
  });
  servicingTotal.font = { bold: true };
  servicing.views = [{ state: 'frozen', ySplit: 1 }];

  const tieOut = workbook.addWorksheet('Principal Tie-Out');
  tieOut.columns = [
    { header: 'Loan #', key: 'loanNumber', width: 16 },
    { header: 'Borrower', key: 'borrowerName', width: 24 },
    { header: 'Lifetime Borrower Principal Received', key: 'borrowerPrincipalReceived', width: 34 },
    { header: 'Investor Principal Settlement', key: 'investorPrincipalSettlement', width: 30 },
    { header: 'Variance', key: 'variance', width: 16 },
    { header: 'Balanced', key: 'balanced', width: 12 }
  ];
  tieOut.getRow(1).eachCell((cell) => {
    cell.font = headerFont;
    cell.fill = headerFill;
  });
  principalTieOuts.forEach((row) => tieOut.addRow({
    ...row,
    balanced: row.balanced ? 'Yes' : 'Review'
  }));
  for (let tieOutColumn = 3; tieOutColumn <= 5; tieOutColumn += 1) {
    tieOut.getColumn(tieOutColumn).numFmt = '$#,##0.00;[Red]($#,##0.00)';
  }
  tieOut.views = [{ state: 'frozen', ySplit: 1 }];

  const trends = workbook.addWorksheet('Monthly Trends');
  trends.columns = [
    { header: 'Month', key: 'month', width: 14 },
    { header: 'Banked Contribution Before Servicing', key: 'bankedContribution', width: 34 },
    { header: 'Brokered Company Revenue', key: 'brokeredCompanyRevenue', width: 28 },
    { header: 'Servicing Income by Receipt Date', key: 'servicingIncome', width: 30 },
    { header: 'Total Company Income', key: 'totalCompanyIncome', width: 24 },
    { header: 'Banked + Brokered Units', key: 'units', width: 24 }
  ];

  const trendsHeaderRow = trends.getRow(1);
  trendsHeaderRow.eachCell((cell) => {
    cell.font = headerFont;
    cell.fill = headerFill;
  });

  const monthlyTrendRows = buildUnifiedRevenueMonthlyTrends(
    loans,
    brokeredLoans,
    servicingEvents,
    period.from,
    period.to
  );
  for (const bucket of monthlyTrendRows) {
    trends.addRow({
      month: bucket.label,
      bankedContribution: bucket.bankedContribution,
      brokeredCompanyRevenue: bucket.brokeredCompanyRevenue,
      servicingIncome: bucket.servicingIncome,
      totalCompanyIncome: bucket.totalCompanyIncome,
      units: bucket.units
    });
  }

  for (const columnKey of ['bankedContribution', 'brokeredCompanyRevenue', 'servicingIncome', 'totalCompanyIncome']) {
    trends.getColumn(columnKey).numFmt = '$#,##0';
    trends.getColumn(columnKey).alignment = { horizontal: 'right' };
  }
  trends.getColumn('units').alignment = { horizontal: 'right' };

  const trendsTotalUnits = monthlyTrendRows.reduce((sum, bucket) => sum + bucket.units, 0);
  const trendsTotalsRow = trends.addRow({
    month: 'TOTAL',
    bankedContribution: monthlyTrendRows.reduce((sum, bucket) => sum + bucket.bankedContribution, 0),
    brokeredCompanyRevenue: monthlyTrendRows.reduce((sum, bucket) => sum + bucket.brokeredCompanyRevenue, 0),
    servicingIncome: monthlyTrendRows.reduce((sum, bucket) => sum + bucket.servicingIncome, 0),
    totalCompanyIncome: monthlyTrendRows.reduce((sum, bucket) => sum + bucket.totalCompanyIncome, 0),
    units: trendsTotalUnits
  });
  trendsTotalsRow.font = { bold: true };

  trends.views = [{ state: 'frozen', ySplit: 1 }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Company_Revenue_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

export function CompanyRevenueReport() {
  const { user } = useAuth();
  const orgId = user?.organization_id;

  const [loans, setLoans] = useState<RevenueReportLoan[]>([]);
  const [brokeredLoans, setBrokeredLoans] = useState<BrokeredRevenueRecord[]>([]);
  const [servicingEvents, setServicingEvents] = useState<ServicingCashEvent[]>([]);
  const [principalTieOuts, setPrincipalTieOuts] = useState<ServicingPrincipalTieOut[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'summary' | 'trends' | 'details' | 'brokered' | 'servicing'>('summary');
  const [searchQuery, setSearchQuery] = useState('');
  const [brokeredSearchQuery, setBrokeredSearchQuery] = useState('');
  const [servicingSearchQuery, setServicingSearchQuery] = useState('');
  const [expandedLoans, setExpandedLoans] = useState<Set<string>>(new Set());
  const [period, setPeriod] = useState<ReportPeriod>(() => makePreset('ytd'));
  const [filters, setFilters] = useState({
    loanOfficer: 'all',
    investor: 'all'
  });

  const fetchData = useCallback(async () => {
    if (!orgId) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await getCompanyRevenueReportData(orgId, {
        from: format(period.from, 'yyyy-MM-dd'),
        to: format(period.to, 'yyyy-MM-dd')
      });
      setLoans(data.bankedLoans);
      setBrokeredLoans(data.brokeredLoans);
      setServicingEvents(data.servicingEvents);
      setPrincipalTieOuts(data.principalTieOuts);
    } catch (error) {
      log.error('Failed to fetch revenue report data', {}, error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsLoading(false);
    }
  }, [period.from, period.to, orgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loanOfficerOptions = useMemo(
    () => [...new Set([
      ...loans.map((loan) => loan.loName),
      ...brokeredLoans.map((loan) => loan.loanOfficerName),
      ...servicingEvents.map((event) => event.loanOfficerName)
    ])].filter((name) => name !== '—').sort(),
    [brokeredLoans, loans, servicingEvents]
  );
  const investorOptions = useMemo(
    () => [...new Set([
      ...loans.map((loan) => loan.investorName),
      ...brokeredLoans.map((loan) => loan.lenderName),
      ...servicingEvents.map((event) => event.investorName)
    ])].filter((name) => name !== '—').sort(),
    [brokeredLoans, loans, servicingEvents]
  );

  // Base set: date (applied server-side at fetch) + LO + investor. Drives the summary cards, the Trends tab,
  // and the header Export — the Loan Detail search must NOT leak into those views.
  const baseFilteredLoans = useMemo(() => filterRevenueReportLoans(loans, {
    loanOfficer: filters.loanOfficer,
    investor: filters.investor,
    searchQuery: ''
  }), [filters.investor, filters.loanOfficer, loans]);

  // Detail set: base + the Loan Detail search box. Used only by the Loan Detail table.
  const detailFilteredLoans = useMemo(() => filterRevenueReportLoans(baseFilteredLoans, {
    loanOfficer: 'all',
    investor: 'all',
    searchQuery
  }), [baseFilteredLoans, searchQuery]);

  const totals = useMemo(() => buildCompanyRevenueDisplayTotals(baseFilteredLoans), [baseFilteredLoans]);

  const baseFilteredBrokeredLoans = useMemo(() => brokeredLoans.filter((loan) => {
    if (filters.loanOfficer !== 'all' && loan.loanOfficerName !== filters.loanOfficer) {
      return false;
    }
    if (filters.investor !== 'all' && loan.lenderName !== filters.investor) {
      return false;
    }
    return true;
  }), [brokeredLoans, filters.investor, filters.loanOfficer]);

  const filteredBrokeredLoans = useMemo(() => baseFilteredBrokeredLoans.filter((loan) => {
    const search = brokeredSearchQuery.trim().toLowerCase();
    return !search || loan.loanNumber.toLowerCase().includes(search)
      || loan.borrowerName.toLowerCase().includes(search);
  }), [baseFilteredBrokeredLoans, brokeredSearchQuery]);

  const baseFilteredServicingEvents = useMemo(() => servicingEvents.filter((event) => {
    if (filters.loanOfficer !== 'all' && event.loanOfficerName !== filters.loanOfficer) {
      return false;
    }
    if (filters.investor !== 'all' && event.investorName !== filters.investor) {
      return false;
    }
    return true;
  }), [filters.investor, filters.loanOfficer, servicingEvents]);

  const filteredServicingEvents = useMemo(() => baseFilteredServicingEvents.filter((event) => {
    const search = servicingSearchQuery.trim().toLowerCase();
    return !search || event.loanNumber.toLowerCase().includes(search)
      || event.borrowerName.toLowerCase().includes(search);
  }), [baseFilteredServicingEvents, servicingSearchQuery]);

  const unifiedTotals = useMemo(
    () => buildUnifiedCompanyRevenueTotals(
      baseFilteredLoans,
      baseFilteredBrokeredLoans,
      baseFilteredServicingEvents
    ),
    [baseFilteredBrokeredLoans, baseFilteredLoans, baseFilteredServicingEvents]
  );

  // Separate totals for the Loan Detail TOTAL row so it ties to the search-filtered rows shown there
  // (identical to `totals` when no search is active).
  const detailTotals = useMemo(() => buildCompanyRevenueDisplayTotals(detailFilteredLoans), [detailFilteredLoans]);

  const monthlyTrends = useMemo(
    () => buildUnifiedRevenueMonthlyTrends(
      baseFilteredLoans,
      baseFilteredBrokeredLoans,
      baseFilteredServicingEvents,
      period.from,
      period.to
    ),
    [baseFilteredBrokeredLoans, baseFilteredLoans, baseFilteredServicingEvents, period.from, period.to]
  );

  // Footer totals tie to the displayed Trends rows.
  const trendsTotals = useMemo(
    () => monthlyTrends.reduce(
      (acc, bucket) => ({
        companyIncome: acc.companyIncome + bucket.totalCompanyIncome,
        banked: acc.banked + bucket.bankedContribution,
        brokered: acc.brokered + bucket.brokeredCompanyRevenue,
        servicing: acc.servicing + bucket.servicingIncome,
        units: acc.units + bucket.units
      }),
      { companyIncome: 0, banked: 0, brokered: 0, servicing: 0, units: 0 }
    ),
    [monthlyTrends]
  );

  const dateLabel = `${format(period.from, 'MM/dd/yyyy')} – ${format(period.to, 'MM/dd/yyyy')}`;
  const exportData = useMemo<CompanyRevenueReportData>(() => ({
    bankedLoans: baseFilteredLoans,
    brokeredLoans: baseFilteredBrokeredLoans,
    servicingEvents: baseFilteredServicingEvents,
    principalTieOuts
  }), [baseFilteredBrokeredLoans, baseFilteredLoans, baseFilteredServicingEvents, principalTieOuts]);

  const toggleExpanded = (loanNumber: string) => {
    setExpandedLoans((previous) => {
      const next = new Set(previous);
      if (next.has(loanNumber)) {
        next.delete(loanNumber);
      } else {
        next.add(loanNumber);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-primary" />
            Company Revenue
          </h1>
          <p className="text-muted-foreground">
            Banked, brokered, and receipt-date servicing income with accountant-ready clearing support
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}>
            <RefreshCw className={cn('h-4 w-4 mr-1', isLoading && 'animate-spin')} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportToExcel(exportData, dateLabel, period)}
            disabled={baseFilteredLoans.length + baseFilteredBrokeredLoans.length + baseFilteredServicingEvents.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <ReportDateRangePicker value={period} onChange={setPeriod} />
        <Select value={filters.loanOfficer} onValueChange={(value) => setFilters((current) => ({ ...current, loanOfficer: value }))}>
          <SelectTrigger className="w-[160px] h-8 text-xs">
            <SelectValue placeholder="All LOs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Loan Officers</SelectItem>
            {loanOfficerOptions.map((loanOfficer) => (
              <SelectItem key={loanOfficer} value={loanOfficer}>
                {loanOfficer}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.investor} onValueChange={(value) => setFilters((current) => ({ ...current, investor: value }))}>
          <SelectTrigger className="w-[160px] h-8 text-xs">
            <SelectValue placeholder="All Investors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Investors</SelectItem>
            {investorOptions.map((investor) => (
              <SelectItem key={investor} value={investor}>
                {investor}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
        <SummaryCard title="Banked Wires" value={formatCurrency(unifiedTotals.bankedResidualWires)} icon={<ArrowLeftRight className="h-5 w-5 text-muted-foreground" />} />
        <SummaryCard
          title="Banked Gross Profit"
          value={formatCurrency(unifiedTotals.bankedContributionBeforeServicing)}
          subtitle="Before receipt-date servicing"
          icon={<TrendingUp className="h-5 w-5 text-green-600" />}
          colorClass="text-green-600"
        />
        <SummaryCard title="Brokered Income" value={formatCurrency(unifiedTotals.brokeredCompanyRevenue)} icon={<DollarSign className="h-5 w-5 text-blue-600" />} colorClass="text-blue-600" />
        <SummaryCard title="Servicing Income" value={formatCurrency(unifiedTotals.servicingInterestIncome)} subtitle="By payment received date" icon={<TrendingUp className="h-5 w-5 text-violet-600" />} colorClass="text-violet-600" />
        <SummaryCard
          title="Company Income"
          value={formatCurrency(unifiedTotals.consolidatedCompanyIncome)}
          subtitle="Servicing counted once"
          icon={<DollarSign className="h-5 w-5 text-emerald-600" />}
          colorClass={unifiedTotals.consolidatedCompanyIncome >= 0 ? 'text-emerald-600' : 'text-red-600'}
        />
        <SummaryCard
          title="Cash Received"
          value={formatCurrency(unifiedTotals.bankedResidualWires + unifiedTotals.brokeredGrossChecks + unifiedTotals.servicingCashReceived)}
          subtitle="Wires + checks + mortgage payments"
          icon={<Hash className="h-5 w-5 text-muted-foreground" />}
        />
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="details">Banked</TabsTrigger>
          <TabsTrigger value="brokered">Brokered</TabsTrigger>
          <TabsTrigger value="servicing">Servicing Income</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <Card>
            <CardHeader>
              <CardTitle>Revenue Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                <div>
                  <h4 className="font-semibold text-green-700 mb-3">Recognized Company Income</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Banked before servicing</span><span className="font-mono">{formatCurrency(unifiedTotals.bankedContributionBeforeServicing)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Brokered</span><span className="font-mono">{formatCurrency(unifiedTotals.brokeredCompanyRevenue)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Servicing received</span><span className="font-mono">{formatCurrency(unifiedTotals.servicingInterestIncome)}</span></div>
                    <div className="flex justify-between border-t pt-1 font-semibold"><span>Total</span><span className="font-mono text-green-700">{formatCurrency(unifiedTotals.consolidatedCompanyIncome)}</span></div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-violet-700 mb-3">Servicing Cash</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Interest income</span><span className="font-mono">{formatCurrency(unifiedTotals.servicingInterestIncome)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Principal clearing</span><span className="font-mono">{formatCurrency(unifiedTotals.servicingPrincipalReceived)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Escrow clearing</span><span className="font-mono">{formatCurrency(unifiedTotals.servicingEscrowReceived)}</span></div>
                    <div className="flex justify-between border-t pt-1 font-semibold"><span>Total cash</span><span className="font-mono text-violet-700">{formatCurrency(unifiedTotals.servicingCashReceived)}</span></div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-blue-700 mb-3">Banked Audit Controls</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Clearing wire effect</span><span className="font-mono">{formatCurrency(totals.passThrough)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Outside-wire clearing</span><span className="font-mono">{formatCurrency(totals.passThroughOutside)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Purchase-basis servicing</span><span className="font-mono">{formatCurrency(unifiedTotals.purchaseBasisServicingInterest)}</span></div>
                    <div className="text-xs text-muted-foreground mt-2">Purchase-basis servicing is a non-additive timing control. See the export for the principal tie-out and QuickBooks bridge.</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : baseFilteredLoans.length + baseFilteredBrokeredLoans.length + baseFilteredServicingEvents.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No recognized company income found for this period.</div>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Monthly Company Income by Source</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={monthlyTrends} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                        <YAxis
                          tick={{ fontSize: 12 }}
                          tickLine={false}
                          axisLine={false}
                          width={70}
                          tickFormatter={(value) => formatCompactCurrency(value as number)}
                        />
                        <Tooltip content={<TrendsTooltip />} />
                        <Legend />
                        <Bar dataKey="totalCompanyIncome" name="Company Income" radius={[3, 3, 0, 0]}>
                          {monthlyTrends.map((bucket) => (
                            <Cell key={bucket.key} fill={bucket.totalCompanyIncome >= 0 ? '#10b981' : '#f43f5e'} />
                          ))}
                        </Bar>
                        <Line type="monotone" dataKey="bankedContribution" name="Banked" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="brokeredCompanyRevenue" name="Brokered" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="servicingIncome" name="Servicing" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Monthly Recognition Detail</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table className="min-w-[760px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[140px]">Month</TableHead>
                        <TableHead className="text-right">Banked</TableHead>
                        <TableHead className="text-right">Brokered</TableHead>
                        <TableHead className="text-right">Servicing</TableHead>
                        <TableHead className="text-right">Company Income</TableHead>
                        <TableHead className="text-right">Units</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthlyTrends.map((bucket) => (
                        <TableRow key={bucket.key}>
                          <TableCell className="font-medium">{bucket.label}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency0(bucket.bankedContribution)}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency0(bucket.brokeredCompanyRevenue)}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency0(bucket.servicingIncome)}</TableCell>
                          <TableCell className={cn('text-right font-mono font-semibold', bucket.totalCompanyIncome >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
                            {formatCurrency0(bucket.totalCompanyIncome)}
                          </TableCell>
                          <TableCell className="text-right">{bucket.units}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell className="font-bold">TOTAL</TableCell>
                        <TableCell className="text-right font-bold font-mono">{formatCurrency0(trendsTotals.banked)}</TableCell>
                        <TableCell className="text-right font-bold font-mono">{formatCurrency0(trendsTotals.brokered)}</TableCell>
                        <TableCell className="text-right font-bold font-mono">{formatCurrency0(trendsTotals.servicing)}</TableCell>
                        <TableCell className="text-right font-bold font-mono">{formatCurrency0(trendsTotals.companyIncome)}</TableCell>
                        <TableCell className="text-right font-bold">{trendsTotals.units}</TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="details">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Loan Detail</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search loan # or borrower..."
                    className="pl-8 h-9"
                    value={brokeredSearchQuery}
                    onChange={(event) => setBrokeredSearchQuery(event.target.value)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : detailFilteredLoans.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">No finalized loans found for this period.</div>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <Table className="min-w-[1560px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[130px]">Loan #</TableHead>
                        <TableHead>Borrower</TableHead>
                        <TableHead>LO</TableHead>
                        <TableHead>Investor</TableHead>
                        <TableHead>Purchased</TableHead>
                        <TableHead className={cn('text-right', WIRE_GROUP_START)}>Wire In</TableHead>
                        <TableHead className="text-right">Less LO Commissions</TableHead>
                        <TableHead className={cn('text-right', WIRE_GROUP_END)}>Net Wire</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                        <TableHead className="text-right">Expenses</TableHead>
                        <TableHead className="text-right">Pass-Through</TableHead>
                        <TableHead className="text-right">Outside Clearing</TableHead>
                        <TableHead className="text-right">Net Profit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detailFilteredLoans.map((loan) => (
                        <DetailRow
                          key={loan.loanNumber}
                          loan={loan}
                          isOpen={expandedLoans.has(loan.loanNumber)}
                          onToggle={() => toggleExpanded(loan.loanNumber)}
                        />
                      ))}
                      <TableRow className="bg-muted/50 font-semibold">
                        <TableCell colSpan={5}>TOTAL ({detailFilteredLoans.length} loans)</TableCell>
                        <TableCell className={cn('text-right font-mono', WIRE_GROUP_START)}>
                          {formatCurrency(detailTotals.wire)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'text-right font-mono',
                            getWireDeductionColorClass(detailTotals.loCompensation)
                          )}
                        >
                          {formatWireDeduction(detailTotals.loCompensation)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'text-right font-mono',
                            WIRE_GROUP_END,
                            detailTotals.netWire >= 0 ? 'text-foreground' : 'text-red-700'
                          )}
                        >
                          {formatCurrency(detailTotals.netWire)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-600">{formatCurrency(detailTotals.revenue)}</TableCell>
                        <TableCell className="text-right font-mono text-red-600">{formatCurrency(detailTotals.expenses)}</TableCell>
                        <TableCell className="text-right font-mono text-blue-600">{formatCurrency(detailTotals.passThrough)}</TableCell>
                        <TableCell className="text-right font-mono text-teal-700">{formatCurrency(detailTotals.passThroughOutside)}</TableCell>
                        <TableCell className={cn('text-right font-mono', detailTotals.netProfit >= 0 ? 'text-green-700' : 'text-red-700')}>
                          {formatCurrency(detailTotals.netProfit)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="brokered">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle>Brokered Income</CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">Recognized by Broker Recon finalized date.</p>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search loan # or borrower..."
                    className="h-9 pl-8"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <BrokeredRevenueTable loans={filteredBrokeredLoans} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="servicing">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle>Servicing Income</CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">Interest is recognized once, by actual payment-received date. Principal and escrow are clearing only.</p>
                  </div>
                  <div className="relative w-64">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search loan # or borrower..."
                      className="h-9 pl-8"
                      value={servicingSearchQuery}
                      onChange={(event) => setServicingSearchQuery(event.target.value)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ServicingRevenueTable events={filteredServicingEvents} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Lifetime Principal Clearing Control</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">Borrower principal received versus investor principal settlement, tied loan by loan.</span>
                  <Badge variant={principalTieOuts.some((row) => !row.balanced) ? 'destructive' : 'outline'}>
                    {principalTieOuts.filter((row) => !row.balanced).length} exception{principalTieOuts.filter((row) => !row.balanced).length === 1 ? '' : 's'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default CompanyRevenueReport;
